import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import NotificationBell from '../components/NotificationBell';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { siteEmploye } from '../utils/organisation';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/* ── Helpers ── */
// Texte affichable d'une valeur qui peut être un objet du référentiel ({ intitule | libelle | nom }).
const texteAffichable = (v) => {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'string' || typeof v === 'number') return String(v);
  return v.intitule || v.libelle || v.nom || null;
};
const fmtHeure = (h) => (h ? h.slice(0, 5) : '--:--');

const fmtDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: 'short' });
};

const dateAujourdhui = () => {
  return new Date().toLocaleDateString('fr-FR', {
    weekday: 'long', day: '2-digit', month: 'long', year: 'numeric'
  });
};

/* ── SVG icônes ── */
const IcoHorloge = ({ color = '#1B5E20' }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="9" stroke={color} strokeWidth="2" />
    <path d="M12 7v5l3 3" stroke={color} strokeWidth="2" strokeLinecap="round" />
  </svg>
);
const IcoCalendrier = ({ color = '#555' }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <rect x="3" y="4" width="18" height="17" rx="2" stroke={color} strokeWidth="2" />
    <path d="M3 9h18M8 2v4M16 2v4" stroke={color} strokeWidth="2" strokeLinecap="round" />
  </svg>
);
const IcoCarte = ({ color = '#1B5E20' }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <rect x="2" y="5" width="20" height="14" rx="3" stroke={color} strokeWidth="2" />
    <path d="M2 10h20" stroke={color} strokeWidth="2" />
    <rect x="5" y="14" width="5" height="2" rx="1" fill={color} />
  </svg>
);
const IcoQR = ({ color = '#555' }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <rect x="3" y="3" width="7" height="7" rx="1" stroke={color} strokeWidth="2" />
    <rect x="14" y="3" width="7" height="7" rx="1" stroke={color} strokeWidth="2" />
    <rect x="3" y="14" width="7" height="7" rx="1" stroke={color} strokeWidth="2" />
    <path d="M14 14h2v2h-2zM18 14h3M14 18h2M18 18h3v3M18 16v2" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);
const IcoUser = ({ color = '#555' }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="8" r="4" stroke={color} strokeWidth="2" />
    <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" stroke={color} strokeWidth="2" strokeLinecap="round" />
  </svg>
);
const IcoGraph = ({ color = '#555' }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path d="M4 20l4-6 4 3 4-8 4-5" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const IcoConges = ({ color = '#555' }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path d="M12 3C8 3 5 7 5 12c0 3.5 2 6.5 5 8" stroke={color} strokeWidth="2" strokeLinecap="round"/>
    <path d="M12 3c4 0 7 4 7 9 0 3.5-2 6.5-5 8" stroke={color} strokeWidth="2" strokeLinecap="round"/>
    <path d="M8 12h8M12 8v8" stroke={color} strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const IcoPrets = ({ color = '#555' }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <rect x="2" y="7" width="20" height="14" rx="2" stroke={color} strokeWidth="2"/>
    <path d="M16 7V5a4 4 0 0 0-8 0v2" stroke={color} strokeWidth="2" strokeLinecap="round"/>
    <path d="M12 13v2M9 15h6" stroke={color} strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const IcoEquipe = ({ color = '#555' }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <circle cx="9" cy="7" r="3" stroke={color} strokeWidth="2"/>
    <path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6" stroke={color} strokeWidth="2" strokeLinecap="round"/>
    <path d="M16 3.13a4 4 0 0 1 0 7.75M21 20c0-3-1.8-5.5-4-6.3" stroke={color} strokeWidth="2" strokeLinecap="round"/>
  </svg>
);
const IcoCheck = ({ color = '#555' }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path d="M9 12l2 2 4-4" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="12" cy="12" r="9" stroke={color} strokeWidth="2"/>
  </svg>
);
const IcoAppel = ({ color = '#555' }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" stroke={color} strokeWidth="2"/>
    <rect x="9" y="3" width="6" height="4" rx="1" stroke={color} strokeWidth="2"/>
    <path d="M9 12h6M9 16h4" stroke={color} strokeWidth="2" strokeLinecap="round"/>
  </svg>
);
const IcoIPM = ({ color = '#1565C0' }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path d="M22 12h-4l-3 9L9 3l-3 9H2" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IcoOeuvreSociale = ({ color = '#C62828' }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="9" cy="7" r="4" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IcoPretSogas = ({ color = '#2E7D32' }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <rect x="2" y="6" width="20" height="14" rx="2" stroke={color} strokeWidth="2"/>
    <path d="M16 6V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v1" stroke={color} strokeWidth="2" strokeLinecap="round"/>
    <path d="M12 12v4M10 14h4" stroke={color} strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

export default function EmployeHomePage() {
  const { employe, token, logout, hasPermission } = useAuth();
  const navigate = useNavigate();
  const { isSupported: pushSupported, permission: pushPerm, subscribed: pushSubscribed, loading: pushLoading, subscribe: subscribePush, unsubscribe: unsubscribePush } = usePushNotifications();
  const [pushBannerDismissed, setPushBannerDismissed] = useState(
    () => localStorage.getItem('sogas_push_banner_dismissed') === '1'
  );
  const [pointageJour, setPointageJour]     = useState(undefined); // undefined=loading, null=absent
  const [dernierPointages, setDernierPointages] = useState([]);
  const [soldeConges, setSoldeConges]       = useState(null);
  const [loading, setLoading]               = useState(true);
  const [monManager, setMonManager]         = useState(null);
  const [mode, setMode]                     = useState('espace');
  // Permissions vivantes lues depuis la DB à chaque chargement de la page
  const [permsVivantes, setPermsVivantes]   = useState(employe?.permissions_effectives || {});

  const hasPostPerms = ['scanner_qr_code', 'valider_conges_n1', 'voir_subordonnes', 'appel_nominal'].some(p => hasPermission(p));

  useEffect(() => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    const charger = async () => {
      try {
        const [resJour, resHisto, resSolde, resProfil, resMe] = await Promise.all([
          axios.get(`${API_URL}/api/pointages/aujourdhui`, { headers }),
          axios.get(`${API_URL}/api/pointages/mon-historique`, { headers }),
          axios.get(`${API_URL}/api/conges/solde`, { headers }),
          employe?.id ? axios.get(`${API_URL}/api/employes/${employe.id}`, { headers }) : Promise.resolve({ data: {} }),
          axios.get(`${API_URL}/api/auth/me`, { headers }),
        ]);
        setPointageJour(resJour.data.pointage || null);
        setDernierPointages((resHisto.data.pointages || []).slice(0, 3));
        setSoldeConges(resSolde.data.solde_disponible ?? resSolde.data.solde ?? null);
        if (resProfil.data.manager) setMonManager(resProfil.data.manager);
        if (resMe.data.employe?.permissions_effectives) setPermsVivantes(resMe.data.employe.permissions_effectives);
      } catch {
        setPointageJour(null);
        setDernierPointages([]);
      } finally {
        setLoading(false);
      }
    };
    charger();
  }, [token]);

  const initiales = employe
    ? `${employe.prenom?.[0] || ''}${employe.nom?.[0] || ''}`.toUpperCase()
    : '??';

  return (
    <Layout>
      <style>{`
        .sg-quick-card {
          background: #fff;
          border-radius: 14px;
          padding: 14px 12px;
          box-shadow: 0 1px 6px rgba(0,0,0,0.06);
          border: 1px solid #f0f0f0;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 6px;
          cursor: pointer;
          transition: transform 0.12s, box-shadow 0.12s;
          -webkit-tap-highlight-color: transparent;
        }
        .sg-quick-card:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,0,0,0.1); }
        .sg-quick-card.main { border: 2px solid #1B5E20; }
        .sg-ico-wrap {
          width: 36px; height: 36px;
          border-radius: 10px;
          display: flex; align-items: center; justify-content: center;
        }
      `}</style>

      <div style={s.page}>

        {/* ══ HEADER ══ */}
        <div className="sg-bandeau-clair" style={s.header}>
          <div style={s.circle} />

          {/* Logo + cloche */}
          <div style={s.headerTop}>
            <img
              src="/logo-sogas1.png"
              alt="SOGAS"
              style={s.logo}
              onError={(e) => { e.target.onerror = null; e.target.src = '/logo-sogas.png'; }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {pushSupported && pushSubscribed && (
                <span title="Notifications push actives" style={{ display: 'flex', alignItems: 'center' }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
                    <path d="M13.73 21a2 2 0 0 1-3.46 0" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
                    <circle cx="18" cy="5" r="4" fill="#4CAF50"/>
                  </svg>
                </span>
              )}
              <NotificationBell couleurIcone="#fff" taille={18} />
            </div>
          </div>

          {/* Profil */}
          <div style={s.profil}>
            <div style={s.avatar}>{initiales}</div>
            <div style={s.profilInfo}>
              <div style={s.profilNom}>{employe?.prenom} {employe?.nom}</div>
              <div style={s.profilSub}>
                {/* site = objet { id, code, nom } depuis le Module 0 : jamais rendu tel quel (« [object Object] »). */}
                {[texteAffichable(employe?.poste), siteEmploye(employe, null), employe?.matricule].filter(Boolean).join(' · ')}
              </div>
            </div>
            <span style={s.badgeActif}>En poste</span>
          </div>
        </div>

        {/* ══ CORPS ══ */}
        <div style={s.body}>

          {/* ── Bannière activation push ── */}
          {pushSupported && !pushSubscribed && pushPerm !== 'denied' && !pushBannerDismissed && (
            <div style={{
              background: 'linear-gradient(135deg, #1B5E20 0%, #2E7D32 100%)',
              borderRadius: 14,
              padding: '12px 14px',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              boxShadow: '0 2px 8px rgba(27,94,32,0.25)',
            }}>
              <div style={{
                width: 36, height: 36, borderRadius: '50%',
                background: 'rgba(255,255,255,0.15)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M13.73 21a2 2 0 0 1-3.46 0" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: '#fff', fontWeight: 700, fontSize: '0.82rem', marginBottom: 2 }}>
                  Activer les notifications
                </div>
                <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.7rem', lineHeight: 1.4 }}>
                  Recevez vos congés, prêts et alertes en temps réel
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                <button
                  onClick={subscribePush}
                  disabled={pushLoading}
                  style={{
                    background: '#fff',
                    color: '#1B5E20',
                    border: 'none',
                    borderRadius: 20,
                    padding: '6px 14px',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    cursor: pushLoading ? 'wait' : 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  {pushLoading ? '…' : 'Activer'}
                </button>
                <button
                  onClick={() => {
                    localStorage.setItem('sogas_push_banner_dismissed', '1');
                    setPushBannerDismissed(true);
                  }}
                  style={{
                    background: 'rgba(255,255,255,0.15)',
                    color: '#fff',
                    border: '1px solid rgba(255,255,255,0.3)',
                    borderRadius: 20,
                    padding: '6px 10px',
                    fontSize: '0.75rem',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  ✕
                </button>
              </div>
            </div>
          )}

          {/* ── Sélecteur de mode (si poste avec permissions) ── */}
          {hasPostPerms && (
            <div style={{ display: 'flex', background: '#fff', borderRadius: 12, padding: 4, gap: 4, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
              <button
                onClick={() => setMode('espace')}
                style={{
                  flex: 1, padding: '9px 0', border: 'none', borderRadius: 9, cursor: 'pointer',
                  background: mode === 'espace' ? '#1B5E20' : 'transparent',
                  color: mode === 'espace' ? '#fff' : '#555',
                  fontWeight: mode === 'espace' ? 700 : 500,
                  fontSize: '0.8rem', fontFamily: 'inherit', transition: 'all 0.15s',
                }}
              >
                Mon Espace
              </button>
              <button
                onClick={() => setMode('site')}
                style={{
                  flex: 1, padding: '9px 0', border: 'none', borderRadius: 9, cursor: 'pointer',
                  background: mode === 'site' ? '#1B5E20' : 'transparent',
                  color: mode === 'site' ? '#fff' : '#555',
                  fontWeight: mode === 'site' ? 700 : 500,
                  fontSize: '0.8rem', fontFamily: 'inherit', transition: 'all 0.15s',
                }}
              >
                {employe?.poste_organigramme?.intitule || 'Mon Site'}
              </button>
            </div>
          )}

          {/* ══ MODE MON SITE ══ */}
          {mode === 'site' && hasPostPerms && (
            <>
              {/* Badge poste */}
              <div style={{ background: '#E8F5E9', borderRadius: 10, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10, border: '1px solid #C8E6C9' }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#1B5E20', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <IcoEquipe color="#fff" />
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#1B5E20' }}>
                    {employe?.poste_organigramme?.intitule || 'Responsable'}
                  </div>
                  <div style={{ fontSize: '0.68rem', color: '#555' }}>Accès responsable actif</div>
                </div>
              </div>

              {/* Actions responsable */}
              <div>
                <p style={s.sectionTitle}>Actions disponibles</p>
                <div style={s.grid}>
                  {hasPermission('scanner_qr_code') && (
                    <div className="sg-quick-card main" onClick={() => navigate('/pointage?mode=scanner')}>
                      <div className="sg-ico-wrap" style={{ background: '#f0f7f0' }}>
                        <IcoQR color="#1B5E20" />
                      </div>
                      <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#1B5E20' }}>Scanner QR</span>
                    </div>
                  )}
                  {hasPermission('appel_nominal') && (
                    <div className="sg-quick-card" onClick={() => navigate('/pointage?mode=appel-nominal')}>
                      <div className="sg-ico-wrap" style={{ background: '#f5f5f5' }}>
                        <IcoAppel color="#555" />
                      </div>
                      <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#555' }}>Appel nominal</span>
                    </div>
                  )}
                  {hasPermission('valider_conges_n1') && (
                    <div className="sg-quick-card" onClick={() => navigate('/conges')}>
                      <div className="sg-ico-wrap" style={{ background: '#f0f4ff' }}>
                        <IcoCheck color="#1565C0" />
                      </div>
                      <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#1565C0' }}>Congés à valider</span>
                    </div>
                  )}
                  {hasPermission('voir_subordonnes') && (
                    <div className="sg-quick-card" onClick={() => navigate('/employes')}>
                      <div className="sg-ico-wrap" style={{ background: '#f5f5f5' }}>
                        <IcoEquipe color="#555" />
                      </div>
                      <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#555' }}>Mon Équipe</span>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {/* ─ Contenu conditionnel selon mode ─ */}
          {(mode === 'espace' || !hasPostPerms) && (
            <>

          {/* ── Statut du jour ── */}
          <div style={s.card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
              <span style={s.sectionLabel}>Aujourd'hui</span>
              <span style={s.dateLabel}>{dateAujourdhui()}</span>
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              {/* Arrivée */}
              <div style={{ ...s.miniCard, background: '#f0f7f0' }}>
                <IcoHorloge color="#1B5E20" />
                <div style={s.miniLabel}>Arrivée</div>
                <div style={{ ...s.miniVal, color: '#1B5E20' }}>
                  {loading ? '…' : fmtHeure(pointageJour?.heure_arrivee)}
                </div>
              </div>

              {/* Départ */}
              <div style={{
                ...s.miniCard,
                background: pointageJour?.heure_depart ? '#f0f7f0' : '#fafafa',
                border: pointageJour?.heure_depart ? 'none' : '1px dashed #e0e0e0',
              }}>
                <IcoHorloge color={pointageJour?.heure_depart ? '#1B5E20' : '#bbb'} />
                <div style={s.miniLabel}>Départ</div>
                <div style={{ ...s.miniVal, color: pointageJour?.heure_depart ? '#1B5E20' : '#bbb' }}>
                  {loading ? '…' : fmtHeure(pointageJour?.heure_depart)}
                </div>
              </div>

              {/* Congés */}
              <div style={{ ...s.miniCard, background: '#fafafa' }}>
                <IcoCalendrier />
                <div style={s.miniLabel}>Congés restants</div>
                <div style={{ ...s.miniVal, color: '#2c2c2c' }}>
                  {loading ? '…' : soldeConges !== null ? `${soldeConges} j.` : '—'}
                </div>
              </div>
            </div>
          </div>

          {/* ── Mon Manager ── */}
          {monManager && (
            <div style={s.card}>
              <p style={{ ...s.sectionLabel, marginBottom: 10 }}>Mon Manager</p>
              <div
                onClick={() => navigate(`/employes/${monManager.id}/profil`)}
                style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', padding: '10px 12px', borderRadius: 10, background: '#f0f7f0', border: '1px solid #c8e6c9' }}
              >
                <div style={{ width: 42, height: 42, borderRadius: '50%', background: '#1B5E20', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <span style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>
                    {`${monManager.prenom?.[0] || ''}${monManager.nom?.[0] || ''}`.toUpperCase()}
                  </span>
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: '#1B5E20' }}>{monManager.prenom} {monManager.nom}</div>
                  <div style={{ fontSize: 12, color: '#555', marginTop: 1 }}>{monManager.poste || '–'}</div>
                </div>
                <div style={{ marginLeft: 'auto', color: '#aaa', fontSize: 20 }}>›</div>
              </div>
            </div>
          )}

          {/* ── Accès rapides ── */}
          <div>
            <p style={s.sectionTitle}>Accès rapides</p>
            <div style={s.grid}>

              {/* ZONE QUOTIDIEN */}
              <div
                className="sg-quick-card main"
                onClick={() => navigate(`/employes/${employe?.id}/profil?tab=carte`)}
              >
                <div className="sg-ico-wrap" style={{ background: '#f0f7f0' }}>
                  <IcoCarte color="#1B5E20" />
                </div>
                <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#1B5E20' }}>Ma Carte</span>
              </div>

              <div
                className="sg-quick-card"
                onClick={() => navigate('/pointage')}
              >
                <div className="sg-ico-wrap" style={{ background: '#f5f5f5' }}>
                  <IcoQR color="#555" />
                </div>
                <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#555' }}>Pointage QR</span>
              </div>

              {/* ZONE MON ARGENT */}
              <div
                className="sg-quick-card"
                onClick={() => navigate('/mes-prets')}
              >
                <div className="sg-ico-wrap" style={{ background: '#fff8e1' }}>
                  <IcoPrets color="#E65100" />
                </div>
                <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#E65100' }}>Mon Argent</span>
              </div>

              {['ipm_gestion', 'ipm_controle_rh', 'ipm_controle_paie', 'ipm_controle_financier', 'ipm_commission', 'ipm_donnees_medicales'].some((k) => permsVivantes?.[k] === true) && (
                <div
                  className="sg-quick-card"
                  onClick={() => navigate('/ipm')}
                >
                  <div className="sg-ico-wrap" style={{ background: '#E3F2FD' }}>
                    <IcoIPM color="#1565C0" />
                  </div>
                  <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#1565C0' }}>IPM</span>
                </div>
              )}

              {/* ZONE MON TEMPS */}
              <div
                className="sg-quick-card"
                onClick={() => navigate('/conges')}
              >
                <div className="sg-ico-wrap" style={{ background: '#f0f4ff' }}>
                  <IcoConges color="#1565C0" />
                </div>
                <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#1565C0' }}>Mes Congés</span>
              </div>

              {/* ZONE AUTRES */}
              {(employe?.role === 'agent_oeuvre_sociale' || permsVivantes?.peut_creer_dette === true) && (
                <div
                  className="sg-quick-card"
                  onClick={() => navigate('/dettes')}
                >
                  <div className="sg-ico-wrap" style={{ background: '#FCE4EC' }}>
                    <IcoOeuvreSociale color="#C62828" />
                  </div>
                  <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#C62828' }}>Oeuvre Sociale</span>
                </div>
              )}

              <div
                className="sg-quick-card"
                onClick={() => navigate(`/employes/${employe?.id}/profil`)}
              >
                <div className="sg-ico-wrap" style={{ background: '#f5f5f5' }}>
                  <IcoUser color="#555" />
                </div>
                <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#555' }}>Mon Espace</span>
              </div>

              <div
                className="sg-quick-card"
                onClick={() => navigate(`/employes/${employe?.id}/evolution`)}
              >
                <div className="sg-ico-wrap" style={{ background: '#f5f5f5' }}>
                  <IcoGraph color="#555" />
                </div>
                <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#555' }}>Mon Évolution</span>
              </div>

              {false /* TODO_REACTIVER */ && (
                <div
                  className="sg-quick-card"
                  onClick={() => navigate('/prets-sogas')}
                >
                  <div className="sg-ico-wrap" style={{ background: '#E8F5E9' }}>
                    <IcoPretSogas color="#2E7D32" />
                  </div>
                  <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#2E7D32' }}>Prêt SOGAS</span>
                </div>
              )}

            </div>
          </div>

          {/* ── Derniers pointages ── */}
          <div style={s.card}>
            <p style={{ ...s.sectionLabel, marginBottom: 10 }}>Derniers pointages</p>

            {loading ? (
              <p style={s.vide}>Chargement…</p>
            ) : dernierPointages.length === 0 ? (
              <p style={s.vide}>Aucun pointage enregistré</p>
            ) : (
              dernierPointages.map((p, i) => (
                <div
                  key={p.id || i}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    paddingBottom: 8,
                    borderBottom: i < dernierPointages.length - 1 ? '0.5px solid #f0f0f0' : 'none',
                    marginBottom: i < dernierPointages.length - 1 ? 8 : 0,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#1B5E20', flexShrink: 0 }} />
                    <span style={{ fontSize: '0.78rem', color: '#333' }}>{fmtDate(p.date)}</span>
                  </div>
                  <span style={{ fontSize: '0.75rem', color: '#888' }}>
                    {fmtHeure(p.heure_arrivee)} → {p.heure_depart ? fmtHeure(p.heure_depart) : 'en cours'}
                  </span>
                </div>
              ))
            )}
          </div>

          </>
          )}

          {/* ── Déconnexion ── */}
          <div
            onClick={() => { logout(); navigate('/login'); }}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              background: 'white',
              border: '1px solid #ffeaea',
              borderRadius: '12px',
              padding: '12px 16px',
              cursor: 'pointer',
              margin: '0 0 12px 0',
              boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9"
                stroke="#C62828" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span style={{ fontSize: '0.78rem', color: '#C62828', fontWeight: '600' }}>
              Se déconnecter
            </span>
          </div>

          {/* ── Copyright ── */}
          <div style={s.copyright}>
            <p style={s.copy1}>© 2025 SOGAS · Tous droits réservés</p>
            <p style={s.copy2}>
              Développé par{' '}
              <span style={{ color: '#1B5E20', fontWeight: 600 }}>Abdoul Aziz Talla</span>
            </p>
          </div>

        </div>
      </div>
    </Layout>
  );
}

const s = {
  page: {
    minHeight: '100vh',
    background: '#f7f7f7',
    display: 'flex',
    flexDirection: 'column',
    maxWidth: 480,
    margin: '0 auto',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    overflowX: 'hidden',
  },

  /* Header */
  header: {
    background: '#1B5E20',
    padding: '14px 18px 20px',
    position: 'relative',
    overflow: 'hidden',
  },
  circle: {
    position: 'absolute',
    top: -20, right: -20,
    width: 100, height: 100,
    background: 'rgba(255,255,255,0.04)',
    borderRadius: '50%',
    pointerEvents: 'none',
  },
  headerTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 14,
    position: 'relative',
    zIndex: 1,
  },
  logo: {
    height: 32,
    objectFit: 'contain',
    display: 'block',
  },
  profil: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    position: 'relative',
    zIndex: 1,
  },
  avatar: {
    width: 44, height: 44,
    background: 'rgba(255,255,255,0.2)',
    borderRadius: '50%',
    border: '2px solid rgba(255,255,255,0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontWeight: 700,
    fontSize: '0.9rem',
    flexShrink: 0,
  },
  profilInfo: { flex: 1, minWidth: 0 },
  profilNom: {
    color: '#fff',
    fontWeight: 700,
    fontSize: '0.95rem',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  profilSub: {
    color: 'rgba(255,255,255,0.75)',
    fontSize: '0.7rem',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  badgeActif: {
    background: 'rgba(255,255,255,0.15)',
    borderRadius: 20,
    padding: '3px 10px',
    color: '#fff',
    fontSize: '0.62rem',
    fontWeight: 600,
    flexShrink: 0,
  },

  /* Corps */
  body: {
    flex: 1,
    padding: 14,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },

  /* Card générique */
  card: {
    background: '#fff',
    borderRadius: 14,
    padding: 14,
    boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
  },

  /* Labels de section */
  sectionLabel: {
    fontSize: '0.72rem',
    fontWeight: 600,
    color: '#555',
    textTransform: 'uppercase',
    letterSpacing: '0.8px',
    margin: 0,
  },
  sectionTitle: {
    fontSize: '0.72rem',
    fontWeight: 600,
    color: '#888',
    textTransform: 'uppercase',
    letterSpacing: '0.8px',
    margin: '0 0 8px 4px',
  },
  dateLabel: {
    fontSize: '0.65rem',
    color: '#999',
    textTransform: 'capitalize',
  },

  /* Mini-cards statut */
  miniCard: {
    flex: 1,
    borderRadius: 10,
    padding: 10,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
  },
  miniLabel: { fontSize: '0.65rem', color: '#888' },
  miniVal:   { fontWeight: 700, fontSize: '0.82rem' },

  /* Grille accès rapides */
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 10,
  },

  /* Pointages */
  vide: {
    textAlign: 'center',
    color: '#bbb',
    fontSize: '0.78rem',
    margin: '8px 0',
  },

  /* Copyright */
  copyright: { textAlign: 'center', padding: '4px 0 8px' },
  copy1: { fontSize: '0.56rem', color: '#ccc', margin: '0 0 3px 0' },
  copy2: { fontSize: '0.55rem', color: '#ccc', margin: 0 },
};
